import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineBaseSchema } from "../machine-base";

const sealProXDesiredPlcSettingsSchema = z.object({
	glueTankTemperature: z.number(),
	glueTankIdleTemperature: z.number(),
	glueHoseTemperature: z.number(),
	glueNozzleTemperature: z.number(),
	glueIdleTime: z.number(),
	maxTimeForPackNetResponse: z.number(),
	consecutiveInductFailuresBeforeError: z.number(),
});

const sealProXMachineConfigurationIdentifiers = ["Unknown", "XF1"] as const;
const sealProXMachineConfigurationIdentifierSchema = z.enum(sealProXMachineConfigurationIdentifiers);

const sealProXPlcSettingsSchema = z.object({
	glueTankTemperature: z.number(),
	glueTankIdleTemperature: z.number(),
	glueHoseTemperature: z.number(),
	glueNozzleTemperature: z.number(),
	glueIdleTime: z.number(),
	maxTimeForPackNetResponse: z.number(),
	consecutiveInductFailuresBeforeError: z.number(),
	machineConfiguration: sealProXMachineConfigurationIdentifierSchema.default("Unknown"),
});

export const sealProXMachineSchema = machineBaseSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.SealProX),
	desiredPlcSettings: sealProXDesiredPlcSettingsSchema.optional(),
	plcSettings: sealProXPlcSettingsSchema.optional(),
});

// TODO @eb remove this function when the SealProX aligns with our decided upon schemas
export const prepareSealProXMachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;

	const iotDevice = {
		hubName: input.iotHubName ?? "",
		deviceId: input.iotEdgeDeviceId ?? "",
		moduleId: input.iotEdgeModuleId ?? "",
		moduleVersion: input.edgeModuleVersion ?? "",
		lastProofOfLife: input.lastProofOfLifeTimeStamp,
	};

	const activeStopReason = input.activeStopReason as Record<string, unknown>;
	let stopReason: {
		stopCode: number;
		message: string;
		arguments: number[];
		isError: boolean;
	} = { stopCode: 0, message: "", arguments: [], isError: false };
	if (typeof activeStopReason === "object" && activeStopReason !== null) {
		stopReason = {
			stopCode: (activeStopReason.stopCode as number) ?? 0,
			message: (activeStopReason.message as string) ?? "",
			arguments: [activeStopReason.argument ?? undefined].filter((a) => a !== undefined).map(Number),
			isError: activeStopReason.isError === true,
		};
	}

	let activeWarnings = Array.isArray(input.warnings) ? input.warnings : [];
	activeWarnings = activeWarnings.map((w: Record<string, unknown>) => ({
		message: w.message,
		code: w.code,
		arguments: [w.argument ?? undefined].filter((a) => a !== undefined).map(Number),
	}));

	let workstations = input.workstations;
	if (!Array.isArray(workstations) || workstations.length < 1) {
		const activeErrors = Array.isArray(input.errors) ? input.errors : [];

		workstations = [
			{
				number: 1,
				alarms: activeErrors.map((e: Record<string, unknown>) => ({
					code: e.code,
					message: e.message,
					arguments: [e.argument ?? undefined].filter((a) => a !== undefined).map(String),
				})),
				warnings: activeWarnings.map((w: Record<string, unknown>) => ({
					...w,
					arguments: Array.isArray(w.arguments) ? w.arguments.map(String) : [],
				})),
				stopReason: {
					code: stopReason.stopCode,
					message: stopReason.message,
					arguments: stopReason.arguments.map(String),
					isError: stopReason.isError,
				},
			},
		];
	}

	return {
		...input,
		machineType: "SealProX",
		iotDevice,
		stopReason,
		activeWarnings,
		workstations,
	};
};

export type SealProXDesiredPlcSettings = z.infer<typeof sealProXDesiredPlcSettingsSchema>;
export type SealProXMachineConfigurationIdentifier = z.infer<typeof sealProXMachineConfigurationIdentifierSchema>;
export type SealProXPlcSettings = z.infer<typeof sealProXPlcSettingsSchema>;
export type SealProXMachine = z.infer<typeof sealProXMachineSchema>;
